import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { getRolePermissions, createNoPermissions } from '../../utils/roleUtils';
import { colors, gradients, spacing, breakpoints } from '../../constants/theme';
import InlineLoading from '../../components/common/InlineLoading';
import { trackPageView } from '../../services/analyticsService';

const DashboardContainer = styled.div`
  min-height: calc(100vh - 48px);
  background: ${colors.background.primary};
  color: ${colors.text.primary};
`;

const MainContent = styled.main`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${spacing.xxl};

  @media (max-width: ${breakpoints.tablet}) {
    padding: ${spacing.lg} ${spacing.md};
    justify-content: flex-start;
    min-height: auto;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: ${spacing.md};
  }
`;

const WelcomeTitle = styled.h1`
  color: ${colors.text.primary};
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: ${spacing.lg};
  background: ${gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: ${breakpoints.tablet}) {
    font-size: 2.5rem;
    margin-bottom: ${spacing.md};
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 2rem;
    margin-bottom: ${spacing.sm};
    line-height: 1.2;
  }
`;

const ComingSoonSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${spacing.xl};
  margin-top: ${spacing.xxl};
  width: 100%;
  max-width: 1200px;
  justify-items: center;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${spacing.lg};
    margin-top: ${spacing.xl};
    max-width: 100%;
  }

  @media (max-width: ${breakpoints.mobile}) {
    gap: ${spacing.md};
    margin-top: ${spacing.lg};
  }
`;

const FeatureCard = styled.div<{ $clickable?: boolean }>`
  background: ${colors.background.paper};
  border: 1px solid ${colors.background.tertiary};
  border-radius: 16px;
  padding: ${spacing.xl};
  transition: all 0.3s ease;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};

  &:hover {
    transform: translateY(-4px);
    border-color: ${colors.primary.main};
    box-shadow: 0 15px 35px rgba(99, 102, 241, 0.2);
  }

  @media (max-width: ${breakpoints.tablet}) {
    padding: ${spacing.lg};
    
    &:hover {
      transform: translateY(-2px);
    }
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: ${spacing.md};
    border-radius: 12px;
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${gradients.secondary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${spacing.lg};
  font-size: 1.5rem;

  @media (max-width: ${breakpoints.tablet}) {
    width: 50px;
    height: 50px;
    margin-bottom: ${spacing.md};
    font-size: 1.3rem;
  }

  @media (max-width: ${breakpoints.mobile}) {
    width: 45px;
    height: 45px;
    margin-bottom: ${spacing.sm};
    font-size: 1.2rem;
  }
`;

const FeatureTitle = styled.h3`
  color: ${colors.text.primary};
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: ${spacing.md};

  @media (max-width: ${breakpoints.tablet}) {
    font-size: 1.3rem;
    margin-bottom: ${spacing.sm};
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.2rem;
  }
`;

const FeatureDescription = styled.p`
  color: ${colors.text.secondary};
  line-height: 1.6;
  margin-bottom: ${spacing.lg};

  @media (max-width: ${breakpoints.tablet}) {
    font-size: 0.95rem;
    margin-bottom: ${spacing.md};
    line-height: 1.5;
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.9rem;
    margin-bottom: ${spacing.sm};
    line-height: 1.4;
  }
`;

const ComingSoonBadge = styled.span`
  background: ${gradients.primary};
  color: ${colors.primary.contrast};
  padding: ${spacing.sm} ${spacing.md};
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: ${spacing.xs} ${spacing.sm};
    font-size: 0.75rem;
  }
`;

const LaunchBadge = styled.span`
  background: ${colors.success};
  color: ${colors.primary.contrast};
  padding: ${spacing.sm} ${spacing.md};
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: ${spacing.xs} ${spacing.sm};
    font-size: 0.75rem;
  }
`;

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();

  // Track page view
  useEffect(() => {
    if (userData) {
      trackPageView('Dashboard', userData.id);
    }
  }, [userData]);

  // Get user permissions based on role, but only after userData is loaded
  // This prevents admin content from flashing during authentication
  const permissions = userData ? getRolePermissions(userData.role) : createNoPermissions();

  // Show loading state while userData is being fetched
  if (!userData) {
    return (
      <DashboardContainer>
        <MainContent>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <InlineLoading message="Loading your dashboard..." />
          </div>
        </MainContent>
      </DashboardContainer>
    );
  }

  const handleFlashCheckClick = () => {
    navigate('/flashcheck');
  };

  const handleAdminPanelClick = () => {
    navigate('/admin');
  };

  return (
    <DashboardContainer>
      <MainContent>
        <WelcomeTitle>Intuition AI Dashboard</WelcomeTitle>
        
        <ComingSoonSection>
          {/* FlashCheck - Only show when permissions are loaded and user can access */}
          {permissions.canAccessFlashCheck && (
            <FeatureCard $clickable onClick={handleFlashCheckClick}>
              <FeatureIcon>⚡</FeatureIcon>
              <FeatureTitle>FlashCheck</FeatureTitle>
              <FeatureDescription>
                AI-powered, agentic tech due diligence, delivering comprehensive insights 10x faster by analyzing hundreds of sources.
              </FeatureDescription>
              <LaunchBadge>Launch Now</LaunchBadge>
            </FeatureCard>
          )}

          {/* Admin Panel - Only show for admin and super_admin roles */}
          {permissions.canAccessAdminPanel && (
            <FeatureCard $clickable onClick={handleAdminPanelClick}>
              <FeatureIcon>⚙️</FeatureIcon>
              <FeatureTitle>Admin Panel</FeatureTitle>
              <FeatureDescription>
                Manage users and credits.
              </FeatureDescription>
              <LaunchBadge>Launch Now</LaunchBadge>
            </FeatureCard>
          )}

          <FeatureCard>
            <FeatureIcon>🎲</FeatureIcon>
            <FeatureTitle>DeepDice</FeatureTitle>
            <FeatureDescription>
              Advanced agentic platform that goes deep in system and dice KPIs that matters. Harness the power of AI to make informed choices with confidence.
            </FeatureDescription>
            <ComingSoonBadge>Coming Soon</ComingSoonBadge>
          </FeatureCard>
        </ComingSoonSection>
      </MainContent>
    </DashboardContainer>
  );
};

export default DashboardPage;
